import { api } from '@/lib/api'

export type AgendaOwner = {
  id: string
  name: string
}

export type AgendaPermission =
  | 'CanInviteMembers'
  | 'CanRemoveMembers'
  | 'CanManagePermissions'
  | 'CanViewEvents'
  | 'CanEditEvents'
  | 'CanViewNotes'
  | 'CanEditNotes'
  | 'CanViewFinancial'
  | 'CanEditFinancial'

export type Agenda = {
  id: string
  name: string
  description: string | null
  image_path: string | null,
  owner: AgendaOwner | null
  role: 'owner' | 'member' | 'admin'
  is_active: boolean
  created_at: string
  permissions: AgendaPermission[]
}

export type CreateAgendaPayload = {
  name: string
  description: string | null
}

export type AgendaDetails = {
  id: string
  name: string
  description: string | null
  image_path: string | null
  owner: AgendaOwner
  is_active: boolean
  created_at: string
  role: 'owner' | 'member' | 'admin'
  permissions: AgendaPermission[]
}

export type UpdateAgendaPayload = {
  name: string
  description: string | null
}

export type AgendaMember = {
  id: number
  agenda_id: string
  user: {
    id: string
    name: string
  }
  role: string
  joined_at: string
  permissions: AgendaPermission[]
}

export const getAgendasService = (): Promise<Agenda[]> =>
  api.get<Agenda[]>('/agendas').then((res) => res.data)

export const getAgendaByIdService = (agendaId: string): Promise<AgendaDetails> =>
  api.get<AgendaDetails>(`/agendas/${agendaId}`).then((res) => res.data)

export const createAgendaService = (payload: CreateAgendaPayload): Promise<Agenda> =>
  api.post<Agenda>('/agendas', payload).then((res) => res.data)

export const updateAgendaService = (agendaId: string, payload: UpdateAgendaPayload): Promise<Agenda> =>
  api.put<Agenda>(`/agendas/${agendaId}`, payload).then((res) => res.data)

export const getAgendaMembersService = (agendaId: string): Promise<AgendaMember[]> =>
  api.get<AgendaMember[]>(`/agendas/${agendaId}/members`).then((res) => res.data)

export const inviteAgendaMemberService = (agendaId: string, email: string): Promise<void> =>
  api.post(`/agendas/${agendaId}/invite`, { email }).then((res) => res.data)

export const acceptInviteService = (token: string): Promise<void> =>
  api.post('/agendas/accept-invite', { token }).then((res) => res.data)

export const removeAgendaMemberService = (agendaId: string, targetUserId: string): Promise<void> =>
  api.delete(`/agendas/${agendaId}/members/${targetUserId}`).then((res) => res.data)

export const promoteAgendaMemberService = (agendaId: string, targetUserId: string): Promise<void> =>
  api.put(`/agendas/${agendaId}/members/${targetUserId}/promote`).then((res) => res.data)

export const demoteAgendaMemberService = (agendaId: string, targetUserId: string): Promise<void> =>
  api.put(`/agendas/${agendaId}/members/${targetUserId}/demote`).then((res) => res.data)

export const transferOwnershipService = (agendaId: string, newOwnerUserId: string): Promise<void> =>
  api.put(`/agendas/${agendaId}/transfer-ownership`, { new_owner_user_id: newOwnerUserId }).then((res) => res.data)

export const updateMemberPermissionsService = (
  agendaId: string,
  targetUserId: string,
  permissions: AgendaPermission[],
): Promise<void> =>
  api.put(`/agendas/${agendaId}/members/${targetUserId}/permissions`, { permissions }).then((res) => res.data)

export const uploadAgendaImageService = (agendaId: string, file: File): Promise<void> => {
  const formData = new FormData()
  formData.append('file', file)
  return api.post(`/agendas/${agendaId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data)
}
